import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import {
  Eye,
  ArrowUpDown,
  MoreHorizontal,
  Copy,
  Archive,
  ArchiveRestore,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import { useTheme } from "../../../context/ThemeContext";
import { motion } from "framer-motion";

const OrderTable = ({
  orders,
  onSort,
  sortConfig,
  onViewDetails,
  onArchive,
  archivedIds = [],
}) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const getStatusStyle = (status) => {
    const styles = {
      new: isDark
        ? "bg-indigo-500/15 text-indigo-400"
        : "bg-indigo-50 text-indigo-700",
      handled: isDark
        ? "bg-emerald-500/15 text-emerald-400"
        : "bg-emerald-50 text-emerald-700",
      paid: isDark
        ? "bg-green-500/15 text-green-400"
        : "bg-green-50 text-green-700",
      pending: isDark
        ? "bg-yellow-500/15 text-yellow-400"
        : "bg-yellow-50 text-yellow-700",
      shipped: isDark
        ? "bg-blue-500/15 text-blue-400"
        : "bg-blue-50 text-blue-700",
      cancelled: isDark
        ? "bg-red-500/15 text-red-400"
        : "bg-red-50 text-red-700",
    };
    return (
      styles[status] ||
      (isDark ? "bg-white/[0.06] text-gray-400" : "bg-gray-100 text-gray-600")
    );
  };

  const getStatusLabel = (status) => {
    const labels = {
      new: "Nieuwe bestelling",
      handled: "Afgehandeld",
      paid: "Betaald",
      pending: "In afwachting",
      shipped: "Verzonden",
      cancelled: "Geannuleerd",
    };
    return labels[status] || status;
  };

  const columns = [
    { key: "id", label: "ID", className: "w-[80px]" },
    { key: "date", label: "Datum" },
    { key: "customer", label: "Klant" },
    { key: "total_amount", label: "Totaal" },
    { key: "status", label: "Status" },
  ];

  return (
    <>
      {/* Mobile card layout */}
      <div className="sm:hidden space-y-3">
        {orders.length === 0 ? (
          <div
            className={`rounded-xl p-8 text-center ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
          >
            <p
              className={`text-[13px] ${isDark ? "text-gray-500" : "text-gray-400"}`}
            >
              Geen bestellingen gevonden.
            </p>
          </div>
        ) : (
          orders.map((order, i) => (
            <motion.div
              key={order.id}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.02 }}
              className={`rounded-xl overflow-hidden ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
            >
              <div className="p-3">
                <div className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p
                        className={`text-[13px] font-semibold truncate ${isDark ? "text-white" : "text-gray-900"}`}
                      >
                        {order.customer_details?.firstName}{" "}
                        {order.customer_details?.lastName}
                      </p>
                      {order.source === "configurator" && (
                        <span
                          className={`text-[10px] font-medium px-1.5 py-0.5 rounded-md flex-shrink-0 ${isDark ? "bg-orange-500/15 text-orange-400" : "bg-orange-50 text-orange-600"}`}
                        >
                          Config
                        </span>
                      )}
                      <span
                        className={`text-[11px] font-medium px-2 py-0.5 rounded-md flex-shrink-0 ${getStatusStyle(order.status)}`}
                      >
                        {getStatusLabel(order.status)}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-1">
                      <span
                        className={`text-[11px] ${isDark ? "text-gray-500" : "text-gray-400"}`}
                      >
                        #
                        {String(order.id).length > 12
                          ? String(order.id).substring(0, 10) + "\u2026"
                          : order.id}
                      </span>
                      <span
                        className={`text-[11px] ${isDark ? "text-gray-500" : "text-gray-400"}`}
                      >
                        {new Date(order.created_at).toLocaleDateString("nl-BE")}
                      </span>
                      <span
                        className={`text-[12px] font-medium ${isDark ? "text-white" : "text-gray-900"}`}
                      >
                        € {parseFloat(order.total_amount).toFixed(2)}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => onViewDetails(order)}
                    className={`h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${isDark ? "text-gray-400 active:bg-white/[0.06]" : "text-gray-500 active:bg-gray-100"}`}
                  >
                    <Eye className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>

      {/* Desktop table layout */}
      <div
        className={`hidden sm:block rounded-xl border overflow-hidden ${isDark ? "bg-white/[0.02] border-white/[0.06]" : "bg-white border-gray-200/80"}`}
      >
        <Table>
          <TableHeader>
            <TableRow
              className={isDark ? "border-white/[0.06]" : "border-gray-100"}
            >
              {columns.map((col) => (
                <TableHead
                  key={col.key}
                  className={`cursor-pointer text-[11px] font-semibold uppercase tracking-wider transition-colors ${col.className || ""} ${isDark ? "text-gray-500 bg-white/[0.02] hover:bg-white/[0.04]" : "text-gray-400 bg-gray-50/50 hover:bg-gray-100"}`}
                  onClick={() => onSort(col.key)}
                >
                  <div className="flex items-center gap-1">
                    {col.label} <ArrowUpDown className="h-3 w-3 opacity-40" />
                  </div>
                </TableHead>
              ))}
              <TableHead
                className={`text-right text-[11px] font-semibold uppercase tracking-wider w-[70px] ${isDark ? "text-gray-500 bg-white/[0.02]" : "text-gray-400 bg-gray-50/50"}`}
              >
                Acties
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="h-32 text-center">
                  <p
                    className={`text-[13px] ${isDark ? "text-gray-500" : "text-gray-400"}`}
                  >
                    Geen bestellingen gevonden.
                  </p>
                </TableCell>
              </TableRow>
            ) : (
              orders.map((order) => (
                <TableRow
                  key={order.id}
                  className={`transition-colors ${isDark ? "border-white/[0.04] hover:bg-white/[0.02]" : "border-gray-50 hover:bg-gray-50/50"}`}
                >
                  <TableCell
                    className={`text-[13px] font-medium ${isDark ? "text-white" : "text-gray-900"}`}
                  >
                    <div className="flex flex-col gap-0.5">
                      <span title={String(order.id)}>
                        #
                        {String(order.id).length > 12
                          ? String(order.id).substring(0, 10) + "\u2026"
                          : order.id}
                      </span>
                      {order.source === "configurator" && (
                        <span
                          className={`text-[10px] font-medium px-1.5 py-0.5 rounded-md w-fit ${isDark ? "bg-orange-500/15 text-orange-400" : "bg-orange-50 text-orange-600"}`}
                        >
                          Configurator
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <span
                        className={`text-[13px] ${isDark ? "text-white" : "text-gray-900"}`}
                      >
                        {new Date(order.created_at).toLocaleDateString("nl-BE")}
                      </span>
                      <span
                        className={`text-[11px] ${isDark ? "text-gray-600" : "text-gray-400"}`}
                      >
                        {new Date(order.created_at).toLocaleTimeString(
                          "nl-BE",
                          {
                            hour: "2-digit",
                            minute: "2-digit",
                          },
                        )}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <span
                        className={`text-[13px] font-medium truncate max-w-[160px] block ${isDark ? "text-white" : "text-gray-900"}`}
                      >
                        {order.customer_details?.firstName}{" "}
                        {order.customer_details?.lastName}
                      </span>
                      <span
                        className={`text-[11px] truncate max-w-[160px] block ${isDark ? "text-gray-600" : "text-gray-400"}`}
                      >
                        {order.customer_details?.email}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell
                    className={`text-[13px] font-medium ${isDark ? "text-white" : "text-gray-900"}`}
                  >
                    € {parseFloat(order.total_amount).toFixed(2)}
                  </TableCell>
                  <TableCell>
                    <span
                      className={`text-[11px] font-medium px-2 py-0.5 rounded-md ${getStatusStyle(order.status)}`}
                    >
                      {getStatusLabel(order.status)}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          className={`h-7 w-7 rounded-lg inline-flex items-center justify-center transition-colors ${isDark ? "text-gray-500 hover:text-white hover:bg-white/[0.06]" : "text-gray-400 hover:text-gray-700 hover:bg-gray-100"}`}
                        >
                          <MoreHorizontal className="h-3.5 w-3.5" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        className={`min-w-[140px] rounded-lg ${isDark ? "bg-[#1a1a1a] border-white/[0.08]" : ""}`}
                      >
                        <DropdownMenuItem
                          onClick={() => onViewDetails(order)}
                          className={`text-[12px] ${isDark ? "text-gray-300 focus:bg-white/[0.06]" : ""}`}
                        >
                          <Eye className="mr-2 h-3.5 w-3.5" /> Details
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            navigator.clipboard.writeText(order.id)
                          }
                          className={`text-[12px] ${isDark ? "text-gray-300 focus:bg-white/[0.06]" : ""}`}
                        >
                          <Copy className="mr-2 h-3.5 w-3.5" /> Kopieer ID
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            navigator.clipboard.writeText(
                              order.customer_details?.email,
                            )
                          }
                          className={`text-[12px] ${isDark ? "text-gray-300 focus:bg-white/[0.06]" : ""}`}
                        >
                          <Copy className="mr-2 h-3.5 w-3.5" /> Kopieer Email
                        </DropdownMenuItem>
                        {onArchive && (
                          <DropdownMenuItem
                            onClick={() => onArchive(order.id)}
                            className={`text-[12px] ${isDark ? "text-gray-300 focus:bg-white/[0.06]" : ""}`}
                          >
                            {archivedIds.includes(String(order.id)) ? (
                              <>
                                <ArchiveRestore className="mr-2 h-3.5 w-3.5" />{" "}
                                Uit archief
                              </>
                            ) : (
                              <>
                                <Archive className="mr-2 h-3.5 w-3.5" />{" "}
                                Archiveren
                              </>
                            )}
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </>
  );
};

export default OrderTable;
